import {
  Box,
  Drawer,
  ScrollArea,
  Stack,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconList } from "@tabler/icons-react";
import React, { ReactNode } from "react";

import { Text, Title } from "@components";
import { SidebarContent } from "@components/SidebarContent/SidebarContent.component";

import * as styles from "./DocsLayout.module.css";

interface NavPage {
  id: string;
  title: string;
}

interface NavSection {
  title: string;
  pages: NavPage[];
}

interface DocsLayoutProps {
  currentPageId: string;
  children: ReactNode;
  sections: NavSection[];
  title: string;
  subtitle?: string;
  basePath: string;
}

export function DocsLayout({
  currentPageId,
  children,
  sections,
  title,
  subtitle,
  basePath,
}: DocsLayoutProps) {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const sidebarHeader = (
    <div className={styles.sidebarHeader}>
      <Title order={5} c="fontLight">
        {title}
      </Title>
      {subtitle && (
        <Text size="xs" c="fontLightSecondary" mt={4} style={{ fontSize: 12 }}>
          {subtitle}
        </Text>
      )}
    </div>
  );

  return (
    <Box mt={{ base: "breathe", md: 220 }} pb="breathe">
      {/* Desktop layout */}
      <div className={styles.desktopLayout}>
        <div className={styles.sidebar}>
          {sidebarHeader}
          <ScrollArea className={styles.sidebarNav} type="hover" scrollbarSize={6}>
            <SidebarContent
              sections={sections}
              currentPageId={currentPageId}
              basePath={basePath}
            />
          </ScrollArea>
        </div>
        <div className={styles.mainArea}>
          <div className={styles.mainContent}>{children}</div>
        </div>
      </div>

      {/* Mobile layout */}
      <Box hiddenFrom="md">
        <Box px="md">{children}</Box>

        {/* Floating nav button */}
        <button
          className={styles.mobileNavButton}
          onClick={openDrawer}
          type="button"
          aria-label="Open documentation navigation"
        >
          <IconList size={22} />
        </button>

        {/* Mobile drawer */}
        <Drawer
          opened={drawerOpened}
          onClose={closeDrawer}
          position="left"
          size={300}
          padding="md"
          overlayProps={{ backgroundOpacity: 0.5 }}
          closeButtonProps={{
            bg: "transparent",
            size: "lg",
          }}
          styles={{
            header: {
              paddingTop: "var(--mantine-spacing-lg)",
              paddingBottom: "var(--mantine-spacing-md)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
              background: "var(--mantine-color-layoutDark-5)",
            },
            body: {
              paddingTop: "var(--mantine-spacing-sm)",
              background: "var(--mantine-color-layoutDark-5)",
            },
            content: {
              background: "var(--mantine-color-layoutDark-5)",
            },
            close: {
              color: "var(--mantine-color-fontLight-5)",
            },
          }}
          title={
            <Stack gap={2}>
              <Title order={5} c="fontLight">
                {title}
              </Title>
              {subtitle && (
                <Text size="xs" c="fontLightSecondary" style={{ fontSize: 12 }}>
                  {subtitle}
                </Text>
              )}
            </Stack>
          }
        >
          <SidebarContent
            sections={sections}
            currentPageId={currentPageId}
            basePath={basePath}
            onNavigate={closeDrawer}
          />
        </Drawer>
      </Box>
    </Box>
  );
}
